import React, { useEffect, useState } from 'react';
import axios from 'axios';

/**
 * Phase 3.A — audit log of every command issued to a gate.
 *
 * Shows each row with:
 *   - When (date + time, Asia/Jerusalem)
 *   - What ("פתיחה" / "סגירה")
 *   - Who ("Manager: שם" or "אוטומטי: Auto-lock")
 *   - Status pill (ממתין / נשלח / בוצע / נכשל / פג תוקף)
 *
 * Pagination uses a stable ``before_id`` cursor: clicking "טען עוד"
 * fetches commands strictly older than the oldest currently shown.
 *
 * Mobile friendly: rows collapse to stacked cards under 600 px.
 */
// How often the open log refreshes itself while mounted, so a command
// fired in another tab — or an automation that just ran — shows up
// without the user having to reload the page. 3 seconds is short
// enough to feel live next to the 1.5s polling the control panel
// already does, and cheap enough to not stress the backend.
const REFRESH_INTERVAL_MS = 3000;


function GateCommandsLog({ gateId, apiBase = 'customer', refreshKey = 0 }) {
  const [items, setItems] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [reachedEnd, setReachedEnd] = useState(false);
  const [error, setError] = useState(null);

  const PAGE_SIZE = 20;

  useEffect(() => {
    let cancelled = false;
    let intervalId = null;

    const fetchPage = (isInitial) => {
      if (isInitial) {
        setItems(null);
        setReachedEnd(false);
      }
      axios.get(`/api/${apiBase}/gates/${gateId}/commands?limit=${PAGE_SIZE}`)
        .then(res => {
          if (cancelled) return;
          const fetched = res.data.items || [];
          // Auto-refresh: replace only the most recent page so the
          // user's "load more" history isn't yanked out from under
          // them. The page-size slice is fine — older commands don't
          // change state.
          setItems(prev => {
            if (!prev) return fetched;
            const oldestVisible = fetched.length > 0 ? fetched[fetched.length - 1].id : null;
            const older = oldestVisible === null
              ? prev
              : prev.filter(c => c.id < oldestVisible);
            return [...fetched, ...older];
          });
          if (isInitial && fetched.length < PAGE_SIZE) setReachedEnd(true);
        })
        .catch(err => {
          if (cancelled || !isInitial) return;
          setError(err.response?.data?.error || 'שגיאה בטעינת הלוג');
        });
    };

    fetchPage(true);
    intervalId = setInterval(() => fetchPage(false), REFRESH_INTERVAL_MS);
    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
    };
  }, [gateId, apiBase, refreshKey]);

  const loadMore = async () => {
    if (loadingMore || !items || reachedEnd) return;
    setLoadingMore(true);
    try {
      const oldest = items[items.length - 1].id;
      const res = await axios.get(
        `/api/${apiBase}/gates/${gateId}/commands?limit=${PAGE_SIZE}&before_id=${oldest}`
      );
      const more = res.data.items || [];
      setItems(prev => [...prev, ...more]);
      if (more.length < PAGE_SIZE) setReachedEnd(true);
    } catch (err) {
      setError(err.response?.data?.error || 'שגיאה בטעינה');
    } finally {
      setLoadingMore(false);
    }
  };

  if (items === null && !error) {
    return <div style={{ color: '#9ca3af', fontSize: '0.85rem', padding: '0.5rem 0' }}>טוען...</div>;
  }
  if (error) {
    return <div style={{ color: '#b91c1c', fontSize: '0.85rem' }}>{error}</div>;
  }
  if (items.length === 0) {
    return <div style={{ color: '#9ca3af', fontSize: '0.85rem', padding: '0.5rem 0' }}>טרם נשלחו פקודות לשער הזה.</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
        {items.map(cmd => <CommandRow key={cmd.id} cmd={cmd} />)}
      </div>
      {!reachedEnd && (
        <button
          type="button"
          onClick={loadMore}
          disabled={loadingMore}
          style={{
            display: 'block',
            width: '100%',
            marginTop: '0.6rem',
            padding: '0.5rem',
            background: '#f7f8fa',
            border: '1px solid #e5e7eb',
            borderRadius: '5px',
            color: '#4a5568',
            fontSize: '0.85rem',
            fontFamily: 'Heebo, sans-serif',
            cursor: loadingMore ? 'wait' : 'pointer',
          }}
        >
          {loadingMore ? 'טוען...' : 'טען עוד'}
        </button>
      )}
    </div>
  );
}


function CommandRow({ cmd }) {
  const when = cmd.issued_at
    ? new Date(cmd.issued_at).toLocaleString('he-IL', {
        timeZone: 'Asia/Jerusalem',
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    : '-';

  const who = cmd.issued_by_label === 'manual'
    ? (cmd.issued_by_name ? `${roleLabel(cmd.issued_by_role)}: ${cmd.issued_by_name}` : 'משתמש')
    : `אוטומטי: ${cmd.issued_by_label_he}`;

  return (
    <div style={{
      display: 'flex',
      flexWrap: 'wrap',
      gap: '0.5rem',
      alignItems: 'center',
      padding: '0.55rem 0.7rem',
      background: '#fff',
      border: '1px solid #e5e7eb',
      borderRadius: '5px',
      fontSize: '0.82rem',
    }}>
      <span style={{ color: '#9ca3af', minWidth: '120px' }}>{when}</span>
      <span style={{ fontWeight: 500, minWidth: '50px' }}>{cmd.command_type_label}</span>
      <span style={{ color: '#4a5568', flex: 1, minWidth: '120px' }}>{who}</span>
      {cmd.is_retry && (
        <span title="ניסיון חוזר אוטומטי לאחר פקודה שלא אושרה" style={{
          fontSize: '0.7rem', color: '#92400e', background: '#fffbeb',
          border: '1px solid #fde68a', borderRadius: '999px',
          padding: '0.1rem 0.45rem', fontWeight: 500,
        }}>↻ ניסיון חוזר</span>
      )}
      {cmd.latency_ms !== null && cmd.latency_ms !== undefined && cmd.status === 'acknowledged' && (
        <span title="זמן מהלחיצה עד אישור הרכיב" style={{
          fontSize: '0.7rem', color: '#475569',
          background: '#f1f5f9', borderRadius: '999px',
          padding: '0.1rem 0.45rem',
        }}>{formatLatency(cmd.latency_ms)}</span>
      )}
      <StatusPill status={cmd.status} statusHe={cmd.status_he} error={cmd.error} />
    </div>
  );
}


function formatLatency(ms) {
  if (ms < 1000) return `${ms} מ״ש`;
  const seconds = ms / 1000;
  return seconds < 10
    ? `${seconds.toFixed(1)} ש׳`
    : `${Math.round(seconds)} ש׳`;
}


function roleLabel(role) {
  switch (role) {
    case 'super_admin': return 'אדמין';
    case 'technician': return 'טכנאי';
    case 'manager': return 'מנהל';
    case 'viewer': return 'צופה';
    default: return 'משתמש';
  }
}


function StatusPill({ status, statusHe, error }) {
  const styleByStatus = {
    pending: { bg: '#fffbeb', fg: '#92400e', border: '#fde68a' },
    sent: { bg: '#eff6ff', fg: '#1e40af', border: '#bfdbfe' },
    acknowledged: { bg: '#ecfdf5', fg: '#047857', border: '#a7f3d0' },
    failed: { bg: '#fef2f2', fg: '#b91c1c', border: '#fecaca' },
    timeout: { bg: '#fef2f2', fg: '#b91c1c', border: '#fecaca' },
  };
  const s = styleByStatus[status] || styleByStatus.pending;
  return (
    <span
      title={error || statusHe}
      style={{
        display: 'inline-block',
        padding: '0.15rem 0.55rem',
        background: s.bg,
        color: s.fg,
        border: `1px solid ${s.border}`,
        borderRadius: '999px',
        fontSize: '0.72rem',
        fontWeight: 500,
        whiteSpace: 'nowrap',
      }}
    >
      {statusHe}
    </span>
  );
}


export default GateCommandsLog;
